/**
 * Shared back-link affordance used by `ProfileLayout`,
 * `ProfileLayoutMobile`, and the profile home's "All profiles"
 * exit link. Centralizes icon size, color, click semantics, and
 * spacing so every profile-scoped surface gets the same chrome.
 *
 * Always renders as an anchor (`component={Link}`) — keeps middle-
 * click / cmd-click / browser back behavior intact, and avoids the
 * onClick-handler-on-IconButton drift the page-level back links
 * had before this consolidation.
 *
 * Layout: 8px between icon and label; small (20px) icon; theme
 * `text.secondary` color that picks up `text.primary` on hover.
 * Caller is responsible for the surrounding wrapper (Container,
 * pt/mt) — those decisions vary between the desktop layout
 * (default Container width) and the home page (md width).
 */
import React from 'react';
import {Box, IconButton, Typography} from '@mui/material';
import {Link} from 'react-router-dom';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';

interface Props {
  to: string;
  label: string;
  ariaLabel?: string;
}

export function BackLink({to, label, ariaLabel}: Props): React.JSX.Element {
  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
      <IconButton
        size="small"
        component={Link}
        to={to}
        aria-label={ariaLabel ?? label}
        sx={{color: 'text.secondary'}}
      >
        <ArrowBackRoundedIcon fontSize="small" />
      </IconButton>
      <Typography
        component={Link}
        to={to}
        sx={{
          fontSize: 13,
          color: 'text.secondary',
          textDecoration: 'none',
          '&:hover': {color: 'text.primary', textDecoration: 'underline'},
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
